import Layout from 'components/layout';

import React, { type FC, type PropsWithChildren } from 'react';

import { graphql, type HeadFC } from 'gatsby';
import {
  AuthorInfo,
  BlogContent,
  Box,
  Container,
  Heading,
  ImageWithPlaceholder,
  Link,
  PreFooterCta,
  ShareIcons,
  Text,
  Icon,
  PostPreview,
  LucidHead,
  NewsletterCta,
} from '@lucid/components';
import { withImage, useSmartlingStrings } from '@lucid/hooks';
import { getReadingTime, isDefined } from '@lucid/utils';

import type { Lucid } from 'types/types';
import { useBlogNewsletter } from 'hooks/hooks';

const BlogPostTemplate: FC<
  PropsWithChildren<
    Lucid.Context.PageProps<
      Lucid.Directus.Generated.Blog_Post_Translations,
      Lucid.Directus.Item,
      Lucid.Directus.Generated.Blog_Post
    >
  >
> = ({ data, pageContext }) => {
  const { directus } = data;
  const node = directus.node;
  const translatedData = node?.translations?.[0];
  const { shouldHideSsr, shortCode, urlLangPrefix, localeId, pageType, nid, metatags, product } = pageContext;
  const { nodeTranslations, display_table_of_contents: willDisplayTableOfContents, image, author, categories } = node;
  const withSmartling = useSmartlingStrings({
    keys: [
      'mktg_blog_read_time',
      'mktg_learning_campus_collection_topics',
      'mktg_blog_about_header',
      'mktg_blog_about_us_chart',
      'mktg_blog_related_articles',
    ],
    shortCode,
  });
  const { newsletterSignUpURL } = useBlogNewsletter(shortCode);
  const { heading, content_blocks, related_posts, pre_footer_heading, pre_footer_link } = translatedData;
  const postCategories = (categories?.map(category => category.blog_category_id) || []).filter(isDefined);

  // Reading Time
  const allText = content_blocks?.reduce((result, block) => {
    if (block.collection === 'blog_html') {
      return `${result} ${(block.item as Lucid.Directus.Generated.Blog_Html).html || ''}`;
    }

    return result;
  }, '');
  const readingTime = getReadingTime(allText || '', shortCode);

  // Related Posts
  const relatedPosts = related_posts
    ?.map(rp => rp.blog_post_id)
    .filter(rp => rp?.translations?.length)
    .filter(isDefined);

  return (
    <Layout
      shouldDisplayBlogNav
      localeId={localeId}
      metatags={metatags}
      nid={nid}
      nodeTranslations={nodeTranslations}
      pageType={pageType}
      product={product}
      shortCode={shortCode}
      shouldHideSsr={shouldHideSsr}
      urlLangPrefix={urlLangPrefix}
    >
      <Box
        css={theme => ({
          backgroundColor: theme.colors.coolGrey[5],
        })}
      >
        <ShareIcons />
        <Container>
          <Box
            css={theme => ({
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              paddingTop: theme.space.s120,
              paddingBottom: theme.space.s120,
            })}
          >
            <Box
              css={theme => ({
                width: '100%',
                flexGrow: '1',
                [theme.breakpoints.bp768.min]: {
                  marginRight: theme.space.s40,
                  maxWidth: '680px',
                  display: 'block',
                  visibility: 'visible',
                  marginTop: 0,
                },
              })}
            >
              {image?.filename_disk && (
                <ImageWithPlaceholder
                  alt={image?.translations?.[0]?.alt || ''}
                  css={theme => ({ display: 'none', [theme.breakpoints.bp768.min]: { display: 'block' } })}
                  src={withImage(image) || ''}
                />
              )}
              <Heading
                as="h1"
                css={theme => ({
                  textAlign: 'center',
                  [theme.breakpoints.bp768.min]: {
                    marginTop: theme.space.s80,
                  },
                  marginBottom: theme.space.s40,
                })}
                variant="hero3XL700"
              >
                {heading}
              </Heading>
              <Box
                css={theme => ({
                  color: theme.colors.orange[90],
                  display: 'flex',
                  flexDirection: 'column',
                  fontWeight: theme.fontWeights.fw500,
                  justifyContent: 'center',
                  gap: theme.space.s24,
                  textAlign: 'center',
                  [theme.breakpoints.bp768.min]: {
                    flexDirection: 'row',
                  },
                })}
              >
                {(author?.first_name || author?.last_name) && <Text>{`${author.first_name} ${author.last_name}`}</Text>}
                <Text>{withSmartling('mktg_blog_read_time', { time: `${readingTime}` })}</Text>
              </Box>
              {categories && categories.length >= 1 && (
                <Box
                  css={theme => ({
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.space.s24,
                    minWidth: '100%',
                    marginTop: theme.space.s40,
                    [theme.breakpoints.bp768.min]: {
                      marginTop: theme.space.s24,
                      flexDirection: 'row',
                    },
                  })}
                >
                  <Box css={{ display: 'flex', flex: '1', minWidth: 'fit-content' }}>
                    <Icon color="coolGrey.100" css={{ margin: '4px' }} type="Tag" />
                    <Text
                      css={theme => ({
                        margin: theme.space.s4,
                        fontWeight: theme.fontWeights.fw600,
                        color: theme.colors.coolGrey[100],
                      })}
                    >
                      {withSmartling('mktg_learning_campus_collection_topics')}
                    </Text>
                  </Box>
                  <ul
                    key="learningCampusCollectionTopicsUL"
                    aria-label={withSmartling('mktg_learning_campus_collection_topics')}
                    css={theme => ({
                      columnGap: theme.space.s16,
                      display: 'grid',
                      justifyContent: 'center',
                      listStyle: 'none',
                      margin: '0',
                      rowGap: theme.space.s8,
                      width: '100%',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(175px, 1fr))',
                    })}
                  >
                    {postCategories.map((category, index) => {
                      if (!category?.translations?.[0]?.name) return null;

                      return (
                        <li key={`${category.id}-${index}`}>
                          <Link
                            css={theme => ({
                              backgroundColor: theme.colors.orange[10],
                              borderRadius: theme.radii.r4,
                              color: theme.colors.coolGrey[100],
                              fontSize: theme.fontSizes.fs12,
                              fontWeight: `${theme.fontWeights.fw500} !important`,
                              margin: '0 0 20px 0',
                              padding: theme.space.s8,
                              textAlign: 'center',
                              width: '100%',
                              [theme.breakpoints.bp768.min]: {
                                maxWidth: '275px',
                              },
                            })}
                            href={category?.translations?.[0]?.path?.url || '/blog'}
                            underline="hover"
                          >
                            {category?.translations?.[0]?.name}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </Box>
              )}
            </Box>
          </Box>
        </Container>
      </Box>
      <Box css={theme => ({ margin: `${theme.space.s120}px 0` })}>
        <Container css={{ position: 'unset' }}>
          <Box css={{ margin: '0 auto', maxWidth: '780px' }}>
            <BlogContent content={content_blocks || []} shouldShowTOC={willDisplayTableOfContents} />
            {author && <AuthorInfo {...author} />}
            <Box
              css={theme => ({
                padding: theme.space.s40,
                borderRadius: theme.radii.r24,
                backgroundColor: theme.colors.coolGrey[5],
              })}
            >
              <Box
                css={theme => ({
                  padding: theme.space.s40,
                  borderRadius: theme.radii.r24,
                  backgroundColor: theme.colors.white,
                })}
              >
                <Heading as="h2" css={{ textAlign: 'center' }} variant="heading2XL">
                  {withSmartling('mktg_blog_about_header', { product: 'Lucidchart' })}
                </Heading>
                <Text variant="paragraphS400">{withSmartling('mktg_blog_about_us_chart')}</Text>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
      {relatedPosts && relatedPosts.length > 0 && (
        <Box
          css={theme => ({
            backgroundColor: theme.colors.orange[5],
            paddingTop: theme.space.s120,
            paddingBottom: theme.space.s120,
          })}
        >
          <Container>
            <Heading
              as="h2"
              css={{
                marginLeft: 'auto',
                marginRight: 'auto',
                maxWidth: '420px',
                textAlign: 'center',
              }}
              variant="heading2XL"
            >
              {withSmartling('mktg_blog_related_articles')}
            </Heading>
            <ul
              css={theme => ({
                flexWrap: 'wrap',
                listStyleType: 'none',
                display: 'flex',
                rowGap: theme.space.s40,
                justifyContent: 'center',
                alignItems: 'center',
                width: 'fit-content',
                margin: 'auto',
                flexDirection: 'column',
                flexShrink: '1',
                [theme.breakpoints.bp768.min]: {
                  flexDirection: 'row',
                  flexBasis: '50%',
                  columnGap: theme.space.s40,
                },
                [theme.breakpoints.bp1220.min]: {
                  flexWrap: 'nowrap',
                  flexBasis: '25%',
                  columnGap: theme.space.s40,
                },
              })}
            >
              {relatedPosts.map(post => (
                <PostPreview key={post.id} {...post} />
              ))}
            </ul>
          </Container>
        </Box>
      )}
      {pre_footer_link?.final_url ? (
        <PreFooterCta backgroundColor="orange.10" ctas={[pre_footer_link]} heading={pre_footer_heading} />
      ) : (
        <NewsletterCta
          css={theme => ({ backgroundColor: theme.colors.orange[10] })}
          newsletterSignUpUrl={newsletterSignUpURL}
        />
      )}
    </Layout>
  );
};

export default BlogPostTemplate;

export const Head: HeadFC<Lucid.Context.HeadData, Lucid.Context.HeadContext> = ({ ...props }) => (
  <LucidHead {...props} />
);

export const query = graphql`
  query BlogPostQuery($nid: ID!, $shortCode: String!) {
    directus {
      node: blog_post_by_id(id: $nid) {
        nodeTranslations: translations {
          path {
            url
          }
          languages_code {
            short_code
          }
          is_published
        }
        date_created
        date_updated
        image {
          ...mediaQuery
        }
        author {
          first_name
          last_name
          image {
            ...mediaQuery
          }
          translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
            bio_html
          }
        }
        categories {
          id
          blog_category_id {
            translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
              name
              path {
                url
              }
            }
          }
        }
        display_table_of_contents
        translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
          heading
          summary
          content_blocks(sort: "order") {
            id
            collection
            item {
              __typename
              ... on DirectusData_blog_featured_snippet {
                id
                heading
                html
              }
              ... on DirectusData_blog_html {
                id
                html
              }
              ... on DirectusData_blog_clickable_image {
                id
                image {
                  ...mediaQuery
                }
                link {
                  final_url
                  default_link_text
                }
                caption
              }
              ... on DirectusData_video {
                id
                preview_image {
                  ...mediaQuery
                }
                video_file {
                  ...mediaQuery
                }
                external_url
                translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
                  alt_description
                }
                alt_text
                should_loop
                should_autoplay
              }
              ... on DirectusData_blog_inline_cta {
                id
                link {
                  final_url
                  default_link_text
                }
                category
                heading
                text
                image {
                  ...mediaQuery
                }
              }
            }
          }
          related_posts {
            blog_post_id {
              id
              status
              image {
                ...mediaQuery
              }
              translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
                heading
                summary
                path {
                  url
                }
              }
            }
          }
          pre_footer_heading
          pre_footer_link {
            final_url
            default_link_text
          }
        }
      }
    }
  }
`;
